import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as signalR from '@microsoft/signalr';
import { Store } from '@ngrx/store';
import { BaseService } from './baseService';
import { StateMessage, StateModel, StateType, StateValueHistory, SubscriptionOptions } from '../models';
import * as StateActions from '../../store/state-items/state-items.actions';
import { environment } from '../../../environments/environment';
import { selectStateUpdates } from '../../store/state-items/state-items.selectors';
import { combineLatest, fromEventPattern, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, map, skip, switchMap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { ConnectionCheck } from '../../utils/connection-check';
import { ApplicationService } from './application.service';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class SignalRService extends BaseService {
  private hubConnection: signalR.HubConnection;
  private activeSubscriptions = new Map<string, { message: StateMessage; subscription: Subscription }>();

  private signalRUrl: string;

  constructor(private http: HttpClient, private store: Store, private applicationService: ApplicationService, private authService: AuthService) {
    super();
    combineLatest([ConnectionCheck.isConnection$, this.authService.isLoggedIn$]).pipe(
      switchMap(([isConnected, isLogged]) => {
        if (isConnected && isLogged) {
          return this.ensureEnvVar().pipe(map(() => true));
        } else {
          return of(false);
        }
      })
    ).subscribe((res) => {
      if (res && !this.hubConnection) {
        this.buildConnection();
        this.startConnection();
      }
    });
  }

  private ensureEnvVar(): Observable<void> {
    return this.applicationService.getEnvironmentVariables().pipe(
      map((env) => {
        this.signalRUrl = env.signalRUrl;
        if (!this.signalRUrl) {
          throw new Error('SignalR URL is not configured properly.');
        }
      })
    );
  }

  subscribeFor(key: string, stateType: StateType, options?: SubscriptionOptions, uniqueId?: string) {
    const message = this.createMessage({ key }, stateType);
    const fullKey = this._getFullKey(key, stateType);
    const uniqueFullKey = `${fullKey}:${uniqueId ?? new uuid()}`;
    this.store.dispatch(StateActions.createState({ key, fullKey: uniqueFullKey, options }));

    if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
      if (this.activeSubscriptions.has(fullKey)) return this.store.select(selectStateUpdates(uniqueFullKey)).pipe(skip(1));

      const subscription = this._subscribe(fullKey, message);
      this.activeSubscriptions.set(fullKey, { message, subscription });
    } else {
      console.warn('Cannot subscribe to SignalR hub: connection is not established');
    }

    return this.store.select(selectStateUpdates(uniqueFullKey)).pipe(skip(1));
  }

  unsubscribe(key: string, stateType: StateType) {
    const fullKey = this._getFullKey(key, stateType);
    if (this.activeSubscriptions.has(fullKey)) {
      const entry = this.activeSubscriptions.get(fullKey);
      entry?.subscription.unsubscribe();
      this.hubConnection.off(fullKey);
      this.activeSubscriptions.delete(fullKey);
    }
  }

  updateState(model: StateModel, stateType: StateType) {
    if (!this.currentUserId) return;
    const message = this.createMessage(model, stateType);
    this.hubConnection.invoke('updateState', message);
  }

  patchState(model: StateModel, stateType: StateType) {
    if (!this.currentUserId) return;
    const message = this.createMessage(model, stateType);
    this.hubConnection.invoke('patchState', message);
  }

  getStateHistory(key: string, options?: SubscriptionOptions): Observable<StateValueHistory[]> {
    const message = this.createMessage({ key: key }, StateType.Platform, options);
    return this.ensureEnvVar().pipe(
      switchMap(() =>
        this.http
        .post<StateValueHistory[]>(`${this.signalRUrl}/api/state/history`, message, {
          headers: {
            ignoreLoader: 'true'
          }
        })
        .pipe(catchError(() => of([])))
      )
    );
  }

  private buildConnection() {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.signalRUrl}/stateHub`)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          return retryContext.previousRetryCount < 5 ? 500 : 1000;
        }
      })
      .build();

    this.hubConnection.onreconnected(() => this.resubscribe());
  }

  private async startConnection() {
    await this.hubConnection
      .start()
      .then(
        () => console.log('SignalR connection started'),
        () => setTimeout(() => this.startConnection(), 1000)
      )
      .catch((ex) => {
        console.log(ex);
      });
  }

  private resubscribe() {
    for (const [fullKey, entry] of this.activeSubscriptions.entries()) {
      this.store.dispatch(
        StateActions.updateStates({
          key: entry.message.data.key,
          fullKey,
          date: entry.message.options?.replaySince
        })
      );
      this._subscribe(fullKey, entry.message);
    }
  }

  private _subscribe(fullKey: string, message: StateMessage) {
    this.hubConnection.off(fullKey);
    this.hubConnection.invoke('subscribe', message).catch((error) => {
      console.error(`Failed to subscribe to ${fullKey}:`, error);
    });

    message.options = { replaySince: new Date() };
    return createHubObservable<any>(this.hubConnection, fullKey)
      .pipe(debounceTime(1000))
      .subscribe((model: StateModel) => {
        if (model.sessionId === this.sessionId) return;

        this.store.dispatch(
          StateActions.updateStates({
            key: message.data.key,
            fullKey,
            date: message.options?.replaySince
          })
        );

        message.options.replaySince = new Date();
        message.options?.replaySince.setSeconds(message.options?.replaySince.getSeconds() - 5);
      });
  }

  createMessage(data: StateModel, authType: StateType, options?: SubscriptionOptions): StateMessage {
    return {
      auth: {
        authType: authType,
        user: this.currentUserId,
        account: this.currentUserId,
        userToken: this.currentUserId,
        platform: window['ed47Platform']
      },
      data: data,
      options
    };
  }

  private _getFullKey(key: string, stateType: StateType) {
    const msg: any = this.createMessage(null, stateType);

    switch (stateType) {
      case StateType.User: {
        return `user(${msg.auth.platform},${msg.auth.user}):${key}`;
      }
      case StateType.Account: {
        return `account(${msg.auth.account}):${key}`;
      }
      case StateType.PlatformUsers: {
        return `users(${msg.auth.platform}):${key}`;
      }
      case StateType.AllUsers: {
        return `allusers(${msg.auth.platform}):${key}`;
      }
      case StateType.Platform: {
        return `platform(${msg.auth.platform}):${key}`;
      }
      case StateType.Public: {
        return `public(${key})`;
      }
      case StateType.Local: {
        return `local(${key})`;
      }
      default: {
        return `default(${key})`;
      }
    }
  }
}

function createHubObservable<T>(hubConnection: signalR.HubConnection, eventKey: string): Observable<T> {
  return fromEventPattern<T>(
    (handler) => hubConnection.on(eventKey, handler),
    (handler) => hubConnection.off(eventKey, handler)
  );
}
